(function () {
  const ROOT_SELECTOR = '[data-vendor-report-root]';
  const REPORT_STORAGE_KEY = 'itmart24_vendor_report_submissions';

  function safeStorageParse(key) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      return;
    }
  }

  class VendorReport {
    constructor(root) {
      this.root = root;
      this.baseUrl = this.resolveBaseUrl();
      this.identity = {
        vendorId: (root.dataset.vendorId || '').trim(),
        vendorHandle: (root.dataset.vendorHandle || '').trim(),
        vendorPageHandle: (root.dataset.vendorPageHandle || '').trim(),
        vendorName: (root.dataset.vendorName || '').trim(),
        vendorPageUrl: (root.dataset.vendorPageUrl || window.location.href).trim(),
      };
      this.customer = {
        customerId: (root.dataset.customerId || '').trim(),
        reporterName: (root.dataset.customerName || '').trim(),
        reporterEmail: (root.dataset.customerEmail || '').trim(),
      };
      this.settings = {
        minDetailsLength: Math.max(1, parseInt(root.dataset.minDetailsLength || '20', 10) || 20),
        maxDetailsLength: Math.max(100, parseInt(root.dataset.maxDetailsLength || '1500', 10) || 1500),
        cooldownHours: Math.max(0, parseInt(root.dataset.cooldownHours || '24', 10) || 24),
        fallbackEmail: (root.dataset.fallbackEmail || '').trim(),
        fallbackSubject: (root.dataset.fallbackSubject || '').trim(),
      };
      this.elements = this.getElements();
      this.boundKeyHandler = this.handleKeydown.bind(this);
      this.lastTrigger = null;

      this.mountModal();
      this.bindEvents();
      this.populateReporterDefaults();
      this.setOpenButtonsExpanded(false);
      this.syncOpenButtonState();
      this.updateFallbackLink();
    }

    getElements() {
      return {
        openButtons: Array.prototype.slice.call(this.root.querySelectorAll('[data-vendor-report-open]')),
        closeButtons: Array.prototype.slice.call(this.root.querySelectorAll('[data-vendor-report-close]')),
        modal: this.root.querySelector('[data-vendor-report-modal]'),
        form: this.root.querySelector('[data-vendor-report-form]'),
        message: this.root.querySelector('[data-vendor-report-message]'),
        feedback: this.root.querySelector('[data-vendor-report-feedback]'),
        submit: this.root.querySelector('[data-vendor-report-submit]'),
        emailLink: this.root.querySelector('[data-vendor-report-email-link]'),
      };
    }

    mountModal() {
      if (!this.elements.modal || this.elements.modal.dataset.vendorReportMounted === 'true') return;

      document.body.appendChild(this.elements.modal);
      this.elements.modal.dataset.vendorReportMounted = 'true';
    }

    hasIdentity() {
      return Boolean(this.identity.vendorId || this.identity.vendorHandle || this.identity.vendorPageHandle);
    }

    resolveBaseUrl() {
      const configured = (this.root.dataset.endpointBase || '').trim().replace(/\/$/, '');
      if (configured) return configured;

      const rootPath =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
        document.documentElement.getAttribute('data-shopify-root') ||
        '/';

      return String(rootPath).replace(/\/$/, '') + '/apps/vendor-reviews';
    }

    requestHeaders() {
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

      if (this.baseUrl.indexOf('ngrok-free.dev') !== -1) headers['ngrok-skip-browser-warning'] = 'true';
      return headers;
    }

    buildRequestErrorMessage(response, path, data) {
      if (data && data.message) return data.message;

      if (response.status === 404) {
        return (
          'Vendor report submission is not configured at ' +
          this.baseUrl +
          path +
          '. Connect the storefront app proxy or vendor moderation endpoint, or use the email fallback below.'
        );
      }

      if (response.status === 401 || response.status === 403) {
        return 'Vendor report submission was rejected by the moderation service. Check app proxy authentication and backend permissions.';
      }

      if (response.status >= 500) {
        return 'Vendor report submission is currently failing on the server. Please try again shortly or use the email fallback.';
      }

      return 'Vendor report request failed with status ' + response.status + '.';
    }

    async apiFetch(path, options) {
      const requestOptions = options || {};
      const response = await fetch(this.baseUrl + path, {
        method: requestOptions.method || 'GET',
        headers: this.requestHeaders(),
        credentials: this.baseUrl.indexOf(window.location.origin) === 0 ? 'same-origin' : 'omit',
        body: requestOptions.body,
      });

      const text = await response.text();
      let data = {};

      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        data = { message: text };
      }

      if (!response.ok) {
        throw new Error(this.buildRequestErrorMessage(response, path, data));
      }

      return data;
    }

    bindEvents() {
      this.elements.openButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.lastTrigger = button;
          this.openModal();
        });
      });

      this.elements.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });

      if (this.elements.form) {
        this.elements.form.addEventListener('submit', (event) => this.submitReport(event));
        this.elements.form.addEventListener('input', () => this.updateFallbackLink());
        this.elements.form.addEventListener('change', () => this.updateFallbackLink());
      }
    }

    populateReporterDefaults() {
      if (!this.elements.form) return;

      if (this.customer.reporterName && this.elements.form.elements.reporterName) {
        this.elements.form.elements.reporterName.value = this.customer.reporterName;
      }

      if (this.customer.reporterEmail && this.elements.form.elements.reporterEmail) {
        this.elements.form.elements.reporterEmail.value = this.customer.reporterEmail;
      }
    }

    getVendorStorageKey() {
      return this.identity.vendorId || this.identity.vendorPageHandle || this.identity.vendorHandle || 'unknown-vendor';
    }

    getLastSubmissionTimestamp() {
      const submissions = safeStorageParse(REPORT_STORAGE_KEY);
      return Number(submissions[this.getVendorStorageKey()] || 0);
    }

    rememberSubmission() {
      const submissions = safeStorageParse(REPORT_STORAGE_KEY);
      submissions[this.getVendorStorageKey()] = Date.now();
      safeStorageSet(REPORT_STORAGE_KEY, submissions);
    }

    isCooldownActive() {
      if (this.settings.cooldownHours <= 0) return false;
      const lastSubmission = this.getLastSubmissionTimestamp();
      if (!lastSubmission) return false;

      const elapsed = Date.now() - lastSubmission;
      return elapsed < this.settings.cooldownHours * 60 * 60 * 1000;
    }

    getCooldownMessage() {
      return (
        'A vendor report from this browser was already submitted recently. Please wait up to ' +
        this.settings.cooldownHours +
        ' hours before sending another report for this vendor.'
      );
    }

    syncOpenButtonState() {
      if (!this.hasIdentity()) {
        this.elements.openButtons.forEach((button) => {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        });
        this.setFeedback('Vendor details are incomplete on this page, so the report form is unavailable.', 'error');
        return;
      }

      if (this.isCooldownActive()) {
        this.setFeedback(this.getCooldownMessage(), 'info');
      }
    }

    setOpenButtonsExpanded(isExpanded) {
      this.elements.openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      });
    }

    setFeedback(message, type) {
      if (!this.elements.feedback) return;

      this.elements.feedback.hidden = !message;
      this.elements.feedback.textContent = message || '';
      this.elements.feedback.dataset.state = type || 'info';
    }

    setReportMessage(message, type) {
      if (!this.elements.message) return;

      this.elements.message.hidden = !message;
      this.elements.message.textContent = message || '';
      this.elements.message.dataset.state = type || 'info';
    }

    setSubmitState(isSubmitting) {
      if (!this.elements.submit) return;

      this.elements.submit.disabled = isSubmitting;
      this.elements.submit.setAttribute('aria-disabled', isSubmitting ? 'true' : 'false');
      this.elements.submit.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      this.elements.submit.textContent = isSubmitting ? 'Submitting...' : 'Submit Report';
    }

    openModal() {
      if (!this.elements.modal || !this.elements.form) return;
      if (!this.hasIdentity()) return;

      this.setReportMessage('', 'info');
      this.updateFallbackLink();
      this.elements.modal.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.setOpenButtonsExpanded(true);
      document.addEventListener('keydown', this.boundKeyHandler);

      if (this.isCooldownActive()) {
        this.setReportMessage(this.getCooldownMessage(), 'info');
      }

      if (this.elements.submit) {
        const isBlocked = this.isCooldownActive();
        this.elements.submit.disabled = isBlocked;
        this.elements.submit.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
      }

      if (typeof window.trapFocus === 'function') {
        const firstField = this.elements.form.querySelector('select, input, textarea, button');
        window.trapFocus(this.elements.modal, firstField || this.elements.modal);
      }

      const firstInput = this.elements.form.querySelector('select, textarea, input:not([type="hidden"])');
      if (firstInput) firstInput.focus();
    }

    closeModal(options) {
      if (!this.elements.modal) return;

      const settings = options || {};
      this.elements.modal.hidden = true;
      document.body.classList.remove('overflow-hidden');
      this.setOpenButtonsExpanded(false);
      document.removeEventListener('keydown', this.boundKeyHandler);

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.lastTrigger || this.elements.openButtons[0] || null);
      }

      if (this.elements.form && settings.resetForm !== false) {
        this.elements.form.reset();
        this.populateReporterDefaults();
        this.updateFallbackLink();
      }

      if (!settings.keepMessage) {
        this.setReportMessage('', 'info');
      }

      if (this.elements.submit) {
        this.elements.submit.disabled = false;
        this.elements.submit.setAttribute('aria-disabled', 'false');
        this.elements.submit.setAttribute('aria-busy', 'false');
        this.elements.submit.textContent = 'Submit Report';
      }
    }

    handleKeydown(event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') {
        this.closeModal();
      }
    }

    updateFallbackLink() {
      if (!this.elements.emailLink || !this.settings.fallbackEmail) return;

      const form = this.elements.form;
      const reasonValue = form && form.elements.reason ? (form.elements.reason.value || '').trim() : '';
      const reasonLabel =
        form && form.elements.reason && form.elements.reason.selectedIndex >= 0
          ? (form.elements.reason.options[form.elements.reason.selectedIndex].text || '').trim()
          : '';
      const details = form && form.elements.details ? (form.elements.details.value || '').trim() : '';
      const reporterName = form && form.elements.reporterName ? (form.elements.reporterName.value || '').trim() : '';
      const reporterEmail = form && form.elements.reporterEmail ? (form.elements.reporterEmail.value || '').trim() : '';

      const lines = [
        'Vendor: ' + this.identity.vendorName,
        'Vendor page: ' + this.identity.vendorPageUrl,
      ];

      if (reasonValue) lines.push('Reason: ' + (reasonLabel || reasonValue));
      if (reporterName) lines.push('Reporter name: ' + reporterName);
      if (reporterEmail) lines.push('Reporter email: ' + reporterEmail);
      lines.push('');
      lines.push(details || 'Please describe the issue with this vendor here.');

      this.elements.emailLink.href =
        'mailto:' +
        encodeURIComponent(this.settings.fallbackEmail) +
        '?subject=' +
        encodeURIComponent(this.settings.fallbackSubject || 'Report Vendor: ' + this.identity.vendorName) +
        '&body=' +
        encodeURIComponent(lines.join('\n'));
    }

    buildPayload() {
      const form = this.elements.form;

      return {
        vendorId: this.identity.vendorId,
        vendorHandle: this.identity.vendorHandle,
        vendorPageHandle: this.identity.vendorPageHandle,
        vendorNameSnapshot: this.identity.vendorName,
        vendorPageUrl: this.identity.vendorPageUrl,
        customerId: this.customer.customerId || (form.elements.customerId.value || '').trim(),
        reporterName: (form.elements.reporterName.value || '').trim(),
        reporterEmail: (form.elements.reporterEmail.value || '').trim(),
        reason: (form.elements.reason.value || '').trim(),
        details: (form.elements.details.value || '').trim(),
        source: (form.elements.source.value || 'storefront_vendor_profile').trim(),
        sessionId: window.ITMART24_SESSION_ID || '',
      };
    }

    async submitReport(event) {
      event.preventDefault();
      if (!this.elements.form || !this.hasIdentity()) return;

      if (this.isCooldownActive()) {
        this.setReportMessage(this.getCooldownMessage(), 'info');
        return;
      }

      if (!this.elements.form.reportValidity()) return;

      const payload = this.buildPayload();
      if (!payload.reason || !payload.details) {
        this.setReportMessage('Please complete the required fields before submitting your report.', 'error');
        return;
      }

      if (payload.details.length < this.settings.minDetailsLength) {
        this.setReportMessage(
          'Please provide at least ' + this.settings.minDetailsLength + ' characters so moderation has enough context.',
          'error'
        );
        return;
      }

      if (payload.details.length > this.settings.maxDetailsLength) {
        this.setReportMessage(
          'Please keep the report details within ' + this.settings.maxDetailsLength + ' characters.',
          'error'
        );
        return;
      }

      this.setSubmitState(true);
      this.setReportMessage('Submitting your report...', 'info');
      let wasSuccessful = false;

      try {
        await this.apiFetch('/vendors/report', {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        wasSuccessful = true;
        this.rememberSubmission();
        this.closeModal({ resetForm: true });
      } catch (error) {
        this.setReportMessage(error.message || 'Unable to submit your vendor report right now.', 'error');
      } finally {
        this.setSubmitState(false);
        if (wasSuccessful) {
          this.setFeedback('Your vendor report has been submitted to moderation for review.', 'success');
        } else {
          this.syncOpenButtonState();
        }
        this.updateFallbackLink();
      }
    }
  }

  function initVendorReports(root) {
    const scope = root || document;
    const nodes = [];

    if (scope.matches && scope.matches(ROOT_SELECTOR)) nodes.push(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach((node) => nodes.push(node));

    nodes.forEach((node) => {
      if (node.dataset.vendorReportInitialized === 'true') return;
      node.dataset.vendorReportInitialized = 'true';
      new VendorReport(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initVendorReports(document);
    });
  } else {
    initVendorReports(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initVendorReports(event.target);
  });
})();
